import React, { useEffect, useState, useCallback } from "react";
import { Spinner, Row, Col, Card, Table, Badge, Alert as BsAlert, Form, Button } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import SafetyScadenzarioService from "../../services/safety/SafetyScadenzarioService";
import AgenziaSelector, { useSafetyAgenziaContext } from "../../components/safety/AgenziaSelector/AgenziaSelector";

const DOMINI = [
    { value: "", label: "Tutti i domini" },
    { value: "ATTESTATI", label: "Attestati / formazione" },
    { value: "VISITE", label: "Visite mediche" },
    { value: "DOCUMENTI", label: "Documenti aziendali" },
    { value: "NOMINE", label: "Nomine" },
    { value: "VALUTAZIONI", label: "Valutazioni rischi" },
    { value: "LICENZE", label: "Titoli aziendali" },
    { value: "ABILITAZIONI", label: "Abilitazioni personale" },
    { value: "DOTAZIONI", label: "Dotazioni" },
    { value: "ATTREZZATURE", label: "Attrezzature" },
    { value: "APPALTI", label: "Appalti" },
    { value: "DPI", label: "DPI" },
    { value: "PARTICOLARI", label: "Lavoratori particolari" },
    { value: "NC", label: "Non conformità" },
];

function fmtDate(d) {
    if (!d) return "—";
    const s = String(d).substring(0, 10);
    const p = s.split("-");
    if (p.length !== 3) return s;
    return `${p[2]}/${p[1]}/${p[0]}`;
}

function parseJson(resp) {
    return resp && typeof resp.json === "function" ? resp.json() : resp;
}

export default function SafetyScadenzarioPage() {
    const navigate = useNavigate();
    const { idAgenzia, setIdAgenzia } = useSafetyAgenziaContext();
    const [items, setItems] = useState([]);
    const [stats, setStats] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [msg, setMsg] = useState(null);
    const [giorni, setGiorni] = useState(60);
    const [dominio, setDominio] = useState("");
    const [stato, setStato] = useState("");
    const [cfg, setCfg] = useState({ email_destinatario: "", giorni_anticipo: 60, attivo: 1 });
    const [savingCfg, setSavingCfg] = useState(false);
    const [sending, setSending] = useState(false);

    const agenziaEff = idAgenzia || "";

    const load = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const opts = { giorni_anticipo: giorni, limit: 300 };
            if (agenziaEff) opts.id_agenzia = agenziaEff;
            if (dominio) opts.dominio = dominio;
            if (stato) opts.stato = stato;
            const [listData, statsData] = await Promise.all([
                parseJson(await SafetyScadenzarioService.list(opts)),
                parseJson(await SafetyScadenzarioService.stats(agenziaEff, giorni)),
            ]);
            setItems(Array.isArray(listData) ? listData : []);
            setStats(statsData && !statsData.message ? statsData : null);
        } catch (err) {
            setItems([]);
            const m = err?.message || "";
            setError(m.includes("NetworkError") || m.includes("Failed to fetch")
                ? "API non raggiungibile. Verifica deploy: SafetyScadenzarioController + Service, auth.json, SQL 2026-06-17."
                : (m || "Errore caricamento scadenzario."));
        } finally {
            setLoading(false);
        }
    }, [agenziaEff, giorni, dominio, stato]);

    const loadConfig = useCallback(async () => {
        if (!agenziaEff) return;
        try {
            const data = await parseJson(await SafetyScadenzarioService.getConfig(agenziaEff));
            if (data && !data.message) {
                setCfg({
                    email_destinatario: data.email_destinatario || "",
                    giorni_anticipo: data.giorni_anticipo ?? 60,
                    attivo: data.attivo ?? 1,
                    invio_automatico: data.invio_automatico ?? 0,
                    ultimo_invio: data.ultimo_invio || null,
                });
            }
        } catch {
            /* config opzionale */
        }
    }, [agenziaEff]);

    useEffect(() => { load(); }, [load]);
    useEffect(() => { loadConfig(); }, [loadConfig]);

    const saveConfig = async () => {
        if (!agenziaEff) {
            setError("Seleziona un'agenzia per configurare le notifiche.");
            return;
        }
        setSavingCfg(true);
        setError(null);
        try {
            await SafetyScadenzarioService.saveConfig({
                id_agenzia: agenziaEff,
                email_destinatario: cfg.email_destinatario,
                giorni_anticipo: cfg.giorni_anticipo,
                attivo: cfg.attivo ? 1 : 0,
            });
            setMsg("Configurazione notifiche salvata.");
            await loadConfig();
        } catch (err) {
            setError(err?.message || "Errore salvataggio configurazione.");
        } finally {
            setSavingCfg(false);
        }
    };

    const inviaPromemoria = async () => {
        if (!agenziaEff) {
            setError("Seleziona un'agenzia per inviare il promemoria.");
            return;
        }
        setSending(true);
        setError(null);
        try {
            const res = await parseJson(await SafetyScadenzarioService.inviaPromemoria(agenziaEff));
            setMsg(res?.messaggio || (res?.inviato ? `Email inviata a ${res.email} (${res.num_scadenze} scadenze).` : "Nessuna email inviata."));
            await loadConfig();
        } catch (err) {
            setError(err?.message || "Errore invio promemoria.");
        } finally {
            setSending(false);
        }
    };

    const openItem = (item) => {
        if (item.link_tab === "lavoratori") {
            navigate("/safety/lavoratori");
        } else if (item.id_azienda) {
            navigate(`/safety/aziende?tab=${item.link_tab || "dati"}`);
        }
    };

    return (
        <div className="p-2 p-md-3">
            <div className="d-flex flex-wrap justify-content-between align-items-center mb-3">
                <div>
                    <h4 className="mb-1"><i className="bi bi-bell me-2 text-warning" />Scadenzario e notifiche</h4>
                    <p className="text-muted small mb-0">Vista unificata di attestati, visite, documenti, verifiche e tutti gli adempimenti con scadenza.</p>
                </div>
                <AgenziaSelector value={idAgenzia} onChange={setIdAgenzia} />
            </div>

            {error && <BsAlert variant="danger" className="py-2" onClose={() => setError(null)} dismissible>{error}</BsAlert>}
            {msg && <BsAlert variant="success" className="py-2" onClose={() => setMsg(null)} dismissible>{msg}</BsAlert>}

            {stats && (
                <Row className="g-2 mb-3">
                    <Col xs={6} md={3}><Card className="border-0 shadow-sm"><Card.Body className="py-2"><div className="text-muted small">Totale in elenco</div><div className="fw-bold fs-5">{stats.totale ?? 0}</div></Card.Body></Card></Col>
                    <Col xs={6} md={3}><Card className="border-0 shadow-sm"><Card.Body className="py-2"><div className="text-muted small">Scadute</div><div className="fw-bold fs-5 text-danger">{stats.scadute ?? 0}</div></Card.Body></Card></Col>
                    <Col xs={6} md={3}><Card className="border-0 shadow-sm"><Card.Body className="py-2"><div className="text-muted small">In scadenza</div><div className="fw-bold fs-5 text-warning">{stats.in_scadenza ?? 0}</div></Card.Body></Card></Col>
                </Row>
            )}

            <Card className="border-0 shadow-sm mb-3">
                <Card.Body>
                    <h6 className="mb-2"><i className="bi bi-funnel me-1" />Filtri</h6>
                    <Row className="g-2 align-items-end">
                        <Col md={2}>
                            <Form.Label className="small">Giorni anticipo</Form.Label>
                            <Form.Control type="number" min="1" max="365" value={giorni} onChange={e => setGiorni(Number(e.target.value) || 60)} />
                        </Col>
                        <Col md={3}>
                            <Form.Label className="small">Dominio</Form.Label>
                            <Form.Select value={dominio} onChange={e => setDominio(e.target.value)}>{DOMINI.map(d => <option key={d.value || "all"} value={d.value}>{d.label}</option>)}</Form.Select>
                        </Col>
                        <Col md={2}>
                            <Form.Label className="small">Stato</Form.Label>
                            <Form.Select value={stato} onChange={e => setStato(e.target.value)}>
                                <option value="">Tutti</option>
                                <option value="SCADUTO">Scaduti</option>
                                <option value="IN_SCADENZA">In scadenza</option>
                            </Form.Select>
                        </Col>
                        <Col md="auto"><Button variant="outline-primary" size="sm" onClick={load} disabled={loading}><i className="bi bi-arrow-clockwise me-1" />Aggiorna</Button></Col>
                    </Row>
                </Card.Body>
            </Card>

            {agenziaEff > 0 && (
                <Card className="border-0 shadow-sm mb-3 border-start border-warning border-3">
                    <Card.Body>
                        <h6 className="mb-2"><i className="bi bi-envelope me-1" />Notifiche email</h6>
                        <Row className="g-2 align-items-end">
                            <Col md={4}>
                                <Form.Label className="small">Destinatario</Form.Label>
                                <Form.Control type="email" value={cfg.email_destinatario} onChange={e => setCfg(c => ({ ...c, email_destinatario: e.target.value }))} />
                            </Col>
                            <Col md={2}>
                                <Form.Label className="small">Giorni anticipo</Form.Label>
                                <Form.Control type="number" value={cfg.giorni_anticipo} onChange={e => setCfg(c => ({ ...c, giorni_anticipo: Number(e.target.value) || 60 }))} />
                            </Col>
                            <Col md={2}>
                                <Form.Check type="switch" id="notif-attivo" label="Attivo" checked={!!cfg.attivo} onChange={e => setCfg(c => ({ ...c, attivo: e.target.checked ? 1 : 0 }))} />
                            </Col>
                            <Col md="auto">
                                <Button variant="primary" size="sm" disabled={savingCfg} onClick={saveConfig} className="me-2">{savingCfg ? <Spinner size="sm" animation="border" /> : "Salva"}</Button>
                                <Button variant="warning" size="sm" disabled={sending} onClick={inviaPromemoria}>{sending ? <Spinner size="sm" animation="border" /> : "Invia promemoria ora"}</Button>
                            </Col>
                        </Row>
                        {cfg.ultimo_invio && <div className="text-muted small mt-2">Ultimo invio: {cfg.ultimo_invio}</div>}
                    </Card.Body>
                </Card>
            )}

            <Card className="border-0 shadow-sm">
                <Card.Body>
                    {loading ? <div className="text-center py-4"><Spinner animation="border" size="sm" /></div>
                        : items.length === 0 ? (
                            <div className="text-center text-muted py-4"><i className="bi bi-check2-circle text-success me-1" />Nessuna scadenza nel periodo selezionato.</div>
                        ) : (
                            <div className="table-responsive">
                                <Table hover size="sm" className="mb-0 align-middle">
                                    <thead className="table-light">
                                        <tr>
                                            <th>Dominio</th>
                                            <th>Adempimento</th>
                                            <th>Azienda</th>
                                            <th>Lavoratore</th>
                                            <th>Scadenza</th>
                                            <th>Stato</th>
                                            <th />
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {items.map((s, idx) => (
                                            <tr key={`${s.dominio}-${s.id_riferimento}-${idx}`}>
                                                <td><Badge bg="light" text="dark" className="border">{s.dominio}</Badge></td>
                                                <td>
                                                    <div>{s.titolo || "—"}</div>
                                                    {s.sottotitolo && <span className="text-muted small">{s.sottotitolo}</span>}
                                                </td>
                                                <td>{s.ragione_sociale || "—"}</td>
                                                <td>{s.lav_cognome ? `${s.lav_cognome} ${s.lav_nome || ""}` : "—"}</td>
                                                <td>{fmtDate(s.data_scadenza)}</td>
                                                <td>
                                                    <Badge bg={s.stato === "SCADUTO" ? "danger" : "warning"} text={s.stato === "SCADUTO" ? "light" : "dark"}>
                                                        {s.stato === "SCADUTO" ? "Scaduto" : "In scadenza"}
                                                    </Badge>
                                                    {typeof s.giorni === "number" && (
                                                        <span className="text-muted small ms-1">({s.giorni < 0 ? `${Math.abs(s.giorni)} gg fa` : `tra ${s.giorni} gg`})</span>
                                                    )}
                                                </td>
                                                <td className="text-end">
                                                    <Button size="sm" variant="outline-primary" onClick={() => openItem(s)}><i className="bi bi-box-arrow-up-right" /></Button>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </Table>
                            </div>
                        )}
                </Card.Body>
            </Card>
        </div>
    );
}
